import AlgoliaCollection from '@components/collection/AlgoliaCollection'
import { Breadcrumbs, Layout, Loader } from '@components/common'
import CategoryNav from '@components/common/CategoryNav'
import { Minus, Plus } from '@components/icons'
import SearchClient, { pathToSearchState } from '@lib/algolia'
import getAllCollections from '@lib/algolia/get-all-collections'
import commerce from '@lib/api/commerce'
import getGlobalStaticProps from '@lib/get-global-static-props'
import { convertSchemaToHtml } from '@lib/helpers/convertSchematoHTML'
import { createClient, getDocByUid } from '@lib/prismicio'
import { PrismicDocument } from '@prismicio/types'
import { AnalyticsPageType } from '@shopify/hydrogen-react'
import cn from 'clsx'
import Cookies from 'js-cookie'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { GetStaticPathsContext } from 'next'
import { NextSeo } from 'next-seo'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { findResultsState } from 'react-instantsearch-dom/server'
import { Banner, FaqBlock } from 'slices'
import { BreadcrumItem } from 'types/Common'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  const config = { locale, locales }

  //added
  let collectionFaqDocument: PrismicDocument | { data: null } = { data: null }
  try {
    collectionFaqDocument = await client.getByUID(
      'landing-page',
      `faq-collection-${params!.slug}`
    )
  } catch (e) {
    console.info(`No prismic document found for collection ${params!.slug}`)
    // console.error(e)
  }

  const collectionPromise = commerce.getCollection({
    variables: { slug: params!.slug },
    config,
    preview,
  })

  const { collection } = await collectionPromise

  if (!collection) {
    return { notFound: true }
  }

  const searchState = {
    collections: [params!.slug],
  }

  const algoliaIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_PRIMARY_INDEX!
  const resultsState = await findResultsState(AlgoliaCollection, {
    searchClient: SearchClient,
    indexName: algoliaIndex,
    searchState,
    defaultCategory: params!.slug,
  })

  const prismicNavSliceToLinks = (slice: any) => {
    const items = Array.isArray(slice?.items) ? slice.items : []

    return [
      { Label: slice?.primary?.label, Link: slice?.primary?.link },
      ...items.map((item: any) => ({ Label: item?.label, Link: item?.link })),
    ].filter((l) => Boolean(l?.Label && l?.Link))
  }

  let collectionNavItems = null
  if (collection.slug) {
    let categoryNavDoc: any = null
    try {
      categoryNavDoc = await client.getSingle('categorynavigations')
    } catch (e) {
      console.info('Unable to load Prismic categorynavigations document')
    }

    const findSliceBySlug = (slug: string) =>
      categoryNavDoc?.data?.slices?.find(
        (slice: any) => slice.primary.slug === slug
      )

    // 1. Direct slug match in Prismic
    const exactSlice = findSliceBySlug(collection.slug)
    if (exactSlice) {
      collectionNavItems = prismicNavSliceToLinks(exactSlice)
    } else {
      // 2. Find which slice lists this collection as a nav item — that slice is the parent
      const collectionLink = `/collections/${collection.slug}`
      const parentSlice = categoryNavDoc?.data?.slices?.find((slice: any) =>
        slice.items?.some((item: any) => item.link === collectionLink)
      )
      if (parentSlice) {
        collectionNavItems = prismicNavSliceToLinks(parentSlice)
      }
    }
  }

  // for brand banner image
  const prismicBrandPageDocPromise = await getDocByUid(
    'brands-page',
    'brands-page',
    client,
    locale
  )

  const prismicBrandPageDoc: PrismicDocument | null =
    await prismicBrandPageDocPromise

  let collectionPageDoc: PrismicDocument | null = null
  try {
    collectionPageDoc = await getDocByUid(
      'landing-page',
      `collection--${params!.slug}`,
      client,
      locale
    )
  } catch (error) {
    // ignore error
  }

  return {
    props: {
      ...globalProps,
      collection: JSON.parse(JSON.stringify(collection)),
      collectionNavItems,
      collectionFaqDocument,
      algoliaIndex,
      resultsState: JSON.parse(JSON.stringify(resultsState)),
      prismicBrandPageData: prismicBrandPageDoc?.data,
      collectionPageData: collectionPageDoc?.data ?? null,
      analyticsPayload: {
        pageType: AnalyticsPageType.collection,
        resourceId: collection.id,
        collectionHandle: collection.slug,
      },
    },
    revalidate: 60,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  // Replace with Shopify lookup or cached cloud function if usage becomes high.
  const collections = await getAllCollections()

  const paths = collections.map((collection: any) => ({
    params: {
      slug: collection.handle,
    },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

interface BrandImageProps {
  url?: string
  dimensions: {
    width: number
    height: number
  }
}

interface BrandListItemProps {
  brandImage?: BrandImageProps
  brandLink: string
  brandName: string
}

interface BrandListProps {
  items: [BrandListItemProps]
  slice_type: string
}

interface BrandPageDataProps {
  slices: [BrandListProps]
}

export default function Collection({
  collection,
  collectionNavItems,
  collectionFaqDocument,
  algoliaIndex,
  resultsState,
  prismicBrandPageData,
  collectionPageData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { data: faqData }: PrismicDocument | { data: null | undefined } =
    collectionFaqDocument
  const { asPath, query, isFallback, pathname } = useRouter()
  const { name, description, descriptionHtml, belowGridContent }: any =
    collection || {}
  const { title: seoTitle, description: seoDescription }: any =
    (collection as any)['seo'] || {}

  const metafieldBelowGridContentValue: string | undefined =
    belowGridContent?.value

  const convertMetafieldBelowGridContentIntoHTML =
    typeof metafieldBelowGridContentValue !== 'string'
      ? ''
      : convertSchemaToHtml(metafieldBelowGridContentValue, true)

  const [readMore, setReadMore] = useState(false)
  const brandPageData: BrandPageDataProps | null =
    (prismicBrandPageData as any) || null
  const collectionHandle = query!.slug as string

  useEffect(() => {
    Cookies.set(
      'cartReturnToLastCollection',
      JSON.stringify({
        handle: collectionHandle,
        title: name,
      }),
      { expires: 5 }
    )
  }, [collectionHandle, name])

  const expandableLink = readMore ? (
    <div className="flex items-center justify-center hover:underline underline-offset-1 cursor-pointer pt-4">
      Read Less <Minus className="ml-2" />
    </div>
  ) : (
    <div className="flex items-center justify-center hover:underline underline-offset-1 cursor-pointer pt-4">
      Read More <Plus className="ml-2" />
    </div>
  )
  const expandableText = !readMore ? 'ReadMore' : 'ReadLess'

  const searchState = pathToSearchState(asPath)

  const titleHyphenReplace = collectionHandle?.replace(/-/g, ' ')

  const options: BreadcrumItem[] = []
  const SHOP_ALL_PARENT = 'Shop All'
  const parentNav = collectionNavItems?.find((item) => {
    return item.Label.includes(SHOP_ALL_PARENT)
  })

  if (parentNav) {
    const endpointPath = asPath.split('?')[0]
    if (parentNav.Link !== endpointPath) {
      options.push({
        link: parentNav.Link,
        label: parentNav.Label.replace(SHOP_ALL_PARENT, '').trim(),
      })
    }
  }

  //added
  let faqBlockSlice: any = null
  if (collectionFaqDocument) {
    faqBlockSlice = faqData?.slices.find(
      (slice: any) => slice.slice_type === 'faq_block'
    )
  }

  if (isFallback) return <Loader title="collection-loader" />

  // https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls?visit_id=638002032430074383-4102162606&hl=en&rd=2

  // for brand banner image
  const brandList: BrandListProps | null =
    brandPageData?.slices?.find(
      (slice: BrandListProps) => slice.slice_type === 'brand_list'
    ) || null

  const theBrand: BrandListItemProps | null =
    brandList?.items.find(
      (item: BrandListItemProps) =>
        item.brandLink.split('/').pop() === collectionHandle
    ) || null

  const brandBannerImage: string | null = theBrand?.brandImage?.url || null

  const bannerSlice = collectionPageData?.slices.find(
    (slice: any) => slice.slice_type === 'banner'
  )

  return (
    <>
      <NextSeo
        title={seoTitle ?? name}
        description={seoDescription ?? description}
        canonical={`https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}/collections/${collectionHandle}`}
      />
      <div>
        <div className="wrapper pb-10" data-id="collection-wrapper">
          <div>
            <Breadcrumbs
              current={name ?? titleHyphenReplace}
              options={options}
            />
          </div>
          {brandBannerImage ? (
            <div className="pb-8">
              {theBrand?.brandImage?.dimensions && (
                <Image
                  src={brandBannerImage}
                  width={theBrand.brandImage.dimensions.width}
                  height={theBrand.brandImage.dimensions.height}
                  alt={name}
                  unoptimized
                />
              )}
            </div>
          ) : bannerSlice ? (
            <Banner slice={bannerSlice} />
          ) : null}
          <h1 className="text-text-dark font-normal text-mobileh4 mt-4 md:mt-6 lg:text-h4">
            {name ? name : titleHyphenReplace}
          </h1>
          {collectionNavItems && <CategoryNav navItems={collectionNavItems} />}
          <AlgoliaCollection
            searchClient={SearchClient}
            indexName={algoliaIndex}
            searchState={searchState}
            resultsState={resultsState}
            defaultCategory={collectionHandle}
            pathname={pathname}
          />
          {metafieldBelowGridContentValue && (
            <div
              className={cn('', {
                'pb-15 lg:pb-20': !faqBlockSlice,
                'pb-0 md:pb-10': faqBlockSlice,
              })}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: convertMetafieldBelowGridContentIntoHTML,
                }}
              />
            </div>
          )}
          <h2 className="text-text-dark uppercase font-normal text-center text-mobileh4 mt-4 md:mt-6 lg:text-h4">
            {name ? name : titleHyphenReplace}
          </h2>
          {descriptionHtml && (
            <div className="text-text-subdued font-normal text-center text-body1 lg:text-body1 mt-4 md:mt-8 md:mb-12 w-full lg:w-10/12 mx-auto">
              <div
                className={cn(
                  !readMore && 'line-clamp-4 md:line-clamp-3 lg:line-clamp-2',
                  'rte-content collection-seo'
                )}
                dangerouslySetInnerHTML={{
                  __html: descriptionHtml.replace('/break/', ''),
                }}
              />
              <a
                onClick={() => {
                  setReadMore(!readMore)
                }}
                title={expandableText}
              >
                {expandableLink}
              </a>
            </div>
          )}
          {faqBlockSlice && (
            <div className="mb-12">
              <FaqBlock slice={faqBlockSlice} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

Collection.Layout = Layout
